import type { DiscoveredService } from '#~/agent/discovery/utils.js';
import type { Discovery } from '#~/device/discovery/index.js';
import { Protocol } from '@studio/models/video';

/** Service object passed from Add Source Continue into Electron direct-link IPC. */
export type DirectLinkableService = (DiscoveredService | Discovery) & {
  address?: string;
  port?: number;
  agentId?: string;
};

/**
 * Advertised Iris URI port from a positively identified Iris video config.
 * RTSP/NDI/local media ports are not control endpoints for /api/v1/link.
 */
const irisAdvertisedVideoPort = (service: DirectLinkableService): number | undefined => {
  if (!('video' in service) || service.video == null) return undefined;
  const video = service.video;
  if (!('protocol' in video) || video.protocol !== Protocol.IRIS) return undefined;
  return typeof video.port === 'number' ? video.port : undefined;
};

/**
 * Shape the Continue payload for Electron's mDNS proxy (`address` / `port`).
 * When the address property is absent, host is `agentId` and port is nested
 * `video.port` (throws if video is missing). On the addressed branch, an
 * explicit top-level port is authoritative; if port is missing, copy nested
 * port only for an Iris video endpoint so the camera POST hits the advertised
 * URI. A present-but-undefined address stays unset so main uses its host
 * fallback.
 */
export const prepareDirectLinkService = (service: DirectLinkableService): DirectLinkableService => {
  const serviceToUse: DirectLinkableService = { ...service };
  if (!('address' in serviceToUse)) {
    // Address-absent Discovery: host is agentId and port is nested video.port.
    // Missing video throws because this branch requires a video endpoint.
    serviceToUse.address = serviceToUse.agentId;
    serviceToUse.port = (serviceToUse as { video: { port: number } }).video.port;
    return serviceToUse;
  }
  if (serviceToUse.port == null) {
    const advertised = irisAdvertisedVideoPort(serviceToUse);
    if (advertised != null) {
      serviceToUse.port = advertised;
    }
  }
  return serviceToUse;
};
